import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import QuickReportButton from "@/components/QuickReportButton";
import { toast } from "sonner";
import {
  PersonSimpleRun, Timer, MapPin, Trophy, Trash, Upload, PlayCircle, StopCircle,
  Coin, Heartbeat, ArrowsClockwise, Eye, EyeSlash, FlagBanner, GearSix, Users, Target,
  UsersThree, Heart, ChatCircle, PaperPlaneRight, Camera, UploadSimple, X, LockKey, LockKeyOpen,
  PencilSimple,
} from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";

// ───── Helpers ─────

function fmtTime(sec) {
  if (!sec || sec < 0) return "00:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function fmtPace(secPerKm) {
  if (!secPerKm || secPerKm <= 0) return "—";
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, "0")} /km`;
}
function fmtKm(meters) { return ((meters || 0) / 1000).toFixed(2); }
function fmtDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } }

// ───── Podcast Coach helpers (Web Speech API — voz nativa en español) ─────
function kmMarkersLen(session) { return (session?.km_markers || []).length; }

const COACH_MOTIVATIONS = [
  "Vas increíble, mantén ese ritmo",
  "Un paso a la vez, tú puedes",
  "Respira, relaja los hombros y sigue",
  "Cada kilómetro te hace más fuerte",
  "Tu único rival es el de ayer",
  "El cansancio es temporal, la gloria es eterna",
  "Vamos, esto es DYD Run",
  "Ese ritmo es de campeón",
  "Tus piernas te llevan, tu mente te empuja",
  "Sigue, la meta se acerca",
];

function pickSpanishVoice() {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  return (
    voices.find((v) => /es-MX|es-419|es_MX/i.test(v.lang)) ||
    voices.find((v) => /^es(-|_|$)/i.test(v.lang)) ||
    voices[0] ||
    null
  );
}

function speak(text) {
  try {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    const v = pickSpanishVoice();
    if (v) u.voice = v;
    u.lang = v?.lang || "es-MX";
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch {/* ignore */}
}

function tryVoiceIntro() {
  // Warm up voices (some browsers load them lazily)
  try { window.speechSynthesis?.getVoices?.(); } catch {}
  speak("Coach D Y D activado. Te narraré tus estadísticas cada kilómetro. ¡A correr!");
}

function speakCoach(kmDone, elapsedSec, distanceM) {
  const min = Math.floor(elapsedSec / 60);
  const sec = Math.round(elapsedSec % 60);
  const timeText = min > 0 ? `${min} minutos y ${sec} segundos` : `${sec} segundos`;
  const paceSec = distanceM > 0 ? elapsedSec / (distanceM / 1000) : 0;
  const pMin = Math.floor(paceSec / 60);
  const pSec = Math.round(paceSec % 60);
  const paceText = paceSec > 0 ? `Ritmo ${pMin} minutos ${pSec} segundos por kilómetro.` : "";
  const motiv = COACH_MOTIVATIONS[Math.floor(Math.random() * COACH_MOTIVATIONS.length)];
  speak(`Kilómetro ${kmDone} completado. Tiempo total ${timeText}. ${paceText} ${motiv}.`);
}

// ───── Live tracker hook ─────

function useGeoTracker(onPoint) {
  const watchIdRef = useRef(null);
  const startedRef = useRef(false);
  const start = useCallback(() => {
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta GPS");
      return false;
    }
    if (watchIdRef.current != null) return true;
    const id = navigator.geolocation.watchPosition(
      (pos) => onPoint({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        alt: pos.coords.altitude,
        accuracy: pos.coords.accuracy,
        t: pos.timestamp / 1000,
      }),
      (err) => { console.error("GPS error", err); toast.error("GPS: " + err.message); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
    watchIdRef.current = id;
    startedRef.current = true;
    return true;
  }, [onPoint]);
  const stop = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);
  useEffect(() => () => stop(), [stop]);
  return { start, stop };
}

function useStepCounter() {
  const [steps, setSteps] = useState(0);
  const lastMagRef = useRef(0);
  const lastPeakAtRef = useRef(0);
  const activeRef = useRef(false);
  const start = useCallback(async () => {
    if (typeof window === "undefined" || !("DeviceMotionEvent" in window)) {
      return false;
    }
    // iOS 13+ needs permission
    try {
      if (typeof DeviceMotionEvent.requestPermission === "function") {
        const p = await DeviceMotionEvent.requestPermission();
        if (p !== "granted") return false;
      }
    } catch { /* ignore */ }
    const handler = (e) => {
      const acc = e.accelerationIncludingGravity || e.acceleration;
      if (!acc) return;
      const mag = Math.sqrt((acc.x || 0) ** 2 + (acc.y || 0) ** 2 + (acc.z || 0) ** 2);
      const now = Date.now();
      // simple peak detection: cross threshold ~12 m/s² after dip
      if (mag > 12 && lastMagRef.current <= 12 && now - lastPeakAtRef.current > 280) {
        lastPeakAtRef.current = now;
        setSteps((s) => s + 1);
      }
      lastMagRef.current = mag;
    };
    window.addEventListener("devicemotion", handler);
    activeRef.current = true;
    return () => { window.removeEventListener("devicemotion", handler); activeRef.current = false; };
  }, []);
  const reset = () => setSteps(0);
  return { steps, start, reset, isActive: () => activeRef.current };
}

// ───── Route mini-map (SVG polyline) ─────

function RouteMap({ polyline, height = 160 }) {
  if (!polyline || polyline.length < 2) {
    return (
      <div className="bg-black/30 border border-white/10 flex items-center justify-center text-xs text-gray-500" style={{ height }}>
        Sin trayecto registrado
      </div>
    );
  }
  const lats = polyline.map((p) => p[0]);
  const lngs = polyline.map((p) => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const dLat = Math.max(0.0001, maxLat - minLat);
  const dLng = Math.max(0.0001, maxLng - minLng);
  const W = 600, H = height;
  const padding = 10;
  const points = polyline.map(([lat, lng]) => {
    const x = padding + ((lng - minLng) / dLng) * (W - 2 * padding);
    const y = padding + ((maxLat - lat) / dLat) * (H - 2 * padding);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full bg-black/30 border border-white/10" style={{ height }}>
      <defs>
        <linearGradient id="trk" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#CCFF00" />
          <stop offset="100%" stopColor="#7af000" />
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke="url(#trk)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
      {polyline.length > 0 && (
        <>
          <circle cx={points.split(" ")[0].split(",")[0]} cy={points.split(" ")[0].split(",")[1]} r="5" fill="#CCFF00" />
          <circle cx={points.split(" ").at(-1).split(",")[0]} cy={points.split(" ").at(-1).split(",")[1]} r="5" fill="#FF6B6B" />
        </>
      )}
    </svg>
  );
}

// ───── Live Tracker tab ─────

function LeafletMap({ points, kmMarkers, height = 220 }) {
  // Dynamic import to avoid SSR issues and keep bundle lazy
  const [Ready, setReady] = useState(null);
  const mapRef = useRef(null);
  const leafletDivRef = useRef(null);
  const layerRef = useRef({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = await import("leaflet");
      await import("leaflet/dist/leaflet.css");
      if (cancelled) return;
      setReady({ L: L.default || L });
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!Ready || !leafletDivRef.current) return;
    const { L } = Ready;
    if (!mapRef.current) {
      mapRef.current = L.map(leafletDivRef.current, { zoomControl: true, attributionControl: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "OpenStreetMap" }).addTo(mapRef.current);
      mapRef.current.setView([20.6633, -88.5678], 15);
    }
    const map = mapRef.current;
    // Clean old layers
    Object.values(layerRef.current).forEach((l) => { try { map.removeLayer(l); } catch {} });
    layerRef.current = {};
    if (points && points.length > 0) {
      const latlngs = points.map((p) => [p.lat, p.lng]);
      const poly = L.polyline(latlngs, { color: "#CCFF00", weight: 4, opacity: 0.9 });
      poly.addTo(map);
      layerRef.current.poly = poly;
      const last = latlngs[latlngs.length - 1];
      const marker = L.circleMarker(last, { radius: 8, color: "#000", fillColor: "#CCFF00", fillOpacity: 1, weight: 2 });
      marker.addTo(map);
      layerRef.current.marker = marker;
      const first = latlngs[0];
      const startM = L.circleMarker(first, { radius: 6, color: "#000", fillColor: "#7af000", fillOpacity: 1, weight: 2 });
      startM.addTo(map);
      layerRef.current.start = startM;
      // km markers with per-km splits (Δ time vs previous km)
      const sortedKms = [...(kmMarkers || [])].sort((a, b) => (a.km || 0) - (b.km || 0));
      sortedKms.forEach((km, idx) => {
        const prevElapsed = idx > 0 ? Number(sortedKms[idx - 1].elapsed || 0) : 0;
        const curElapsed = Number(km.elapsed || 0);
        const splitSec = Math.max(0, curElapsed - prevElapsed);
        const sm = Math.floor(splitSec / 60);
        const ss = Math.round(splitSec % 60);
        const splitStr = curElapsed > 0 ? `${sm}:${String(ss).padStart(2, "0")}` : "";
        const kMk = L.marker([km.lat, km.lng], {
          icon: L.divIcon({
            className: "km-marker",
            html: `<div style="display:flex;flex-direction:column;align-items:center;gap:2px;pointer-events:none;">
              <div style="background:#111;border:2px solid #CCFF00;color:#CCFF00;padding:2px 6px;border-radius:6px;font-weight:900;font-size:11px;white-space:nowrap;">${km.km}km</div>
              ${splitStr ? `<div style="background:#CCFF00;color:#000;padding:1px 5px;border-radius:4px;font-weight:800;font-size:10px;font-family:'JetBrains Mono',monospace;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.4);">${splitStr}</div>` : ""}
            </div>`,
            iconSize: [56, 34], iconAnchor: [28, 17],
          }),
        });
        kMk.addTo(map);
        layerRef.current[`km_${km.km}`] = kMk;
      });
      map.fitBounds(poly.getBounds(), { padding: [25, 25], maxZoom: 17 });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        map.setView([pos.coords.latitude, pos.coords.longitude], 16);
        const marker = L.circleMarker([pos.coords.latitude, pos.coords.longitude], { radius: 8, color: "#000", fillColor: "#CCFF00", fillOpacity: 1, weight: 2 });
        marker.addTo(map);
        layerRef.current.marker = marker;
      }, () => {}, { enableHighAccuracy: true, timeout: 6000 });
    }
  }, [Ready, points, kmMarkers]);
  return <div ref={leafletDivRef} className="rounded overflow-hidden border border-white/10 bg-black/30" style={{ height, width: "100%" }} data-testid="tracker-map" />;
}

// ───── Live Tracker tab (server-side persistent session + Leaflet map) ─────

function LiveTrackerTab({ onFinished }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uiElapsed, setUiElapsed] = useState(0);
  const [lastCoords, setLastCoords] = useState(null);
  const watchIdRef = useRef(null);
  // Wake Lock — keep screen awake while running (user-toggle)
  const [wakeLockOn, setWakeLockOn] = useState(() => {
    try { return localStorage.getItem("dydrun_wakelock") === "1"; } catch { return false; }
  });
  const wakeLockRef = useRef(null);
  // Podcast Coach — voice announcements per km (premium)
  const [coachOn, setCoachOn] = useState(() => {
    try { return localStorage.getItem("dydrun_coach") === "1"; } catch { return false; }
  });
  const [coachPaywallOpen, setCoachPaywallOpen] = useState(false);
  const lastAnnouncedKmRef = useRef(0);
  const coachEnabled = !!user?.podcast_coach_enabled;

  const active = session && ["active", "paused"].includes(session.status);
  const paused = session?.status === "paused";

  // On mount: try to resume any active/paused session
  useEffect(() => {
    api.get("/dyd-run/tracker/active").then(({ data }) => {
      if (data?.active && data?.session) {
        setSession(data.session);
        toast.info("Reanudando carrera activa…");
      }
    }).catch(() => {});
  }, []);

  // UI stopwatch tick (source of truth = server started_at)
  useEffect(() => {
    if (!session || session.status === "finished") { setUiElapsed(0); return; }
    const tick = () => {
      const started = new Date(session.started_at).getTime();
      const pausedSec = Number(session.total_paused_seconds || 0);
      let extraPause = 0;
      if (session.status === "paused" && session.paused_at) {
        extraPause = Math.max(0, (Date.now() - new Date(session.paused_at).getTime()) / 1000);
      }
      let elapsed = Math.floor((Date.now() - started) / 1000 - pausedSec - extraPause);
      if (session.status === "paused") {
        // freeze UI at paused_at
        elapsed = Math.floor((new Date(session.paused_at).getTime() - started) / 1000 - pausedSec);
      }
      setUiElapsed(Math.max(0, elapsed));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [session]);

  // Poll server every 8s to sync distance/points/km markers
  useEffect(() => {
    if (!active) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/dyd-run/tracker/${session.id}`);
        setSession(data);
      } catch {}
    }, 8000);
    return () => clearInterval(id);
  }, [active, session?.id]);

  // GPS watch: post point every 3s if moved >5m (server also filters)
  useEffect(() => {
    if (!active || paused) {
      if (watchIdRef.current !== null) { navigator.geolocation?.clearWatch(watchIdRef.current); watchIdRef.current = null; }
      return;
    }
    if (!navigator.geolocation) { toast.error("Tu navegador no soporta GPS"); return; }
    let lastSent = 0;
    watchIdRef.current = navigator.geolocation.watchPosition(async (pos) => {
      const now = Date.now();
      if (now - lastSent < 3000) return;
      lastSent = now;
      const lat = pos.coords.latitude, lng = pos.coords.longitude, acc = pos.coords.accuracy;
      setLastCoords({ lat, lng, acc });
      try {
        const { data } = await api.post(`/dyd-run/tracker/${session.id}/point`, { lat, lng, accuracy: acc });
        setSession(data);
      } catch {}
    }, () => {}, { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 });
    return () => { if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; };
  }, [active, paused, session?.id]);

  // ── Wake Lock: keep the phone screen on while the race is running ──
  useEffect(() => {
    const acquire = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener?.("release", () => { wakeLockRef.current = null; });
      } catch {/* denied or unsupported */}
    };
    const release = async () => {
      try { await wakeLockRef.current?.release?.(); } catch {}
      wakeLockRef.current = null;
    };
    if (wakeLockOn && active && !paused) {
      acquire();
      const onVis = () => { if (document.visibilityState === "visible" && wakeLockOn && active && !paused) acquire(); };
      document.addEventListener("visibilitychange", onVis);
      return () => { document.removeEventListener("visibilitychange", onVis); release(); };
    } else {
      release();
    }
  }, [wakeLockOn, active, paused]);

  // ── Podcast Coach: speak KPIs + motivational when a new km is reached ──
  useEffect(() => {
    if (!coachOn || !coachEnabled || !active) return;
    if (!("speechSynthesis" in window)) return;
    const kmDone = kmMarkersLen(session);
    if (kmDone > lastAnnouncedKmRef.current) {
      lastAnnouncedKmRef.current = kmDone;
      speakCoach(kmDone, uiElapsed, session?.distance_m || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.km_markers?.length, coachOn, coachEnabled, active]);

  // Reset the km-announcement counter each time a new session starts
  useEffect(() => {
    if (!active) lastAnnouncedKmRef.current = 0;
  }, [active, session?.id]);

  const toggleWakeLock = (v) => {
    setWakeLockOn(v);
    try { localStorage.setItem("dydrun_wakelock", v ? "1" : "0"); } catch {}
    if (v && !("wakeLock" in navigator)) toast.warning("Tu navegador no soporta Wake Lock — usa Chrome/Edge/Safari 16.4+");
    else if (v) toast.success("Pantalla siempre encendida durante la carrera");
  };
  const toggleCoach = (v) => {
    if (v && !coachEnabled) { setCoachPaywallOpen(true); return; }
    setCoachOn(v);
    try { localStorage.setItem("dydrun_coach", v ? "1" : "0"); } catch {}
    if (v && !("speechSynthesis" in window)) toast.warning("Tu navegador no soporta voz");
    else if (v) { toast.success("Coach de voz activado · te narrará cada km"); tryVoiceIntro(); }
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dyd-run/tracker/start");
      setSession(data);
      toast.success("¡Carrera iniciada! El cronómetro corre en el servidor — puedes cerrar la app y regresar.");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al iniciar"); }
    finally { setBusy(false); }
  };
  const pause = async () => {
    try { const { data } = await api.post(`/dyd-run/tracker/${session.id}/pause`); setSession(data); toast.info("Carrera en pausa"); }
    catch (e) { toast.error("Error"); }
  };
  const resume = async () => {
    try { const { data } = await api.post(`/dyd-run/tracker/${session.id}/resume`); setSession(data); toast.success("Carrera reanudada"); }
    catch (e) { toast.error("Error"); }
  };
  const stop = async () => {
    if (!window.confirm("¿Terminar la carrera?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dyd-run/tracker/${session.id}/stop`);
      toast.success(`¡Carrera terminada! ${fmtKm(data.distance_m)}km en ${fmtTime(data.elapsed_seconds)}`);
      window.dispatchEvent(new Event("dyd-coin-refresh"));
      setSession(null); setUiElapsed(0);
      onFinished?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al detener"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("¿Cancelar la sesión sin guardar carrera?")) return;
    try { await api.delete(`/dyd-run/tracker/${session.id}`); toast.info("Sesión cancelada"); setSession(null); setUiElapsed(0); }
    catch { toast.error("Error"); }
  };

  const distanceM = Number(session?.distance_m || 0);
  const pace = useMemo(() => (distanceM < 100 || uiElapsed < 5) ? null : uiElapsed / (distanceM / 1000), [distanceM, uiElapsed]);
  const points = session?.points || [];
  const kmMarkers = session?.km_markers || [];

  return (
    <div className="space-y-4" data-testid="live-tracker">
      <div className="bg-gradient-to-br from-[#CCFF00]/10 to-transparent border border-[#CCFF00]/30 p-4 md:p-6 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Tiempo</div>
            <div className="text-4xl md:text-5xl font-['Barlow_Condensed'] font-black text-white font-mono" data-testid="live-time">{fmtTime(uiElapsed)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Distancia</div>
            <div className="text-4xl md:text-5xl font-['Barlow_Condensed'] font-black text-[#CCFF00] font-mono" data-testid="live-distance">{fmtKm(distanceM)}<span className="text-lg ml-1">km</span></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Ritmo</div>
            <div className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-black text-white font-mono">{fmtPace(pace)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Kilómetros</div>
            <div className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-black text-white font-mono">{kmMarkers.length}<span className="text-xs text-gray-500 ml-2">Vueltas: {session?.laps || 0}</span></div>
          </div>
        </div>

        <LeafletMap points={points} kmMarkers={kmMarkers} height={260} />

        <div className="mt-4 flex flex-wrap gap-2">
          {!active ? (
            <Button onClick={start} disabled={busy} size="lg" className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 flex-1 md:flex-none md:px-8" data-testid="btn-start-run">
              <PlayCircle size={22} weight="fill" className="mr-2" /> Iniciar carrera
            </Button>
          ) : (
            <>
              {!paused ? (
                <Button onClick={pause} size="lg" variant="outline" className="border-yellow-500/50 text-yellow-400" data-testid="btn-pause-run">
                  ⏸ Pausar
                </Button>
              ) : (
                <Button onClick={resume} size="lg" className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-resume-run">
                  ▶ Reanudar
                </Button>
              )}
              <Button onClick={stop} disabled={busy} size="lg" variant="destructive" className="flex-1 md:flex-none md:px-8" data-testid="btn-stop-run">
                <StopCircle size={22} weight="fill" className="mr-2" /> Terminar
              </Button>
              <Button onClick={cancel} disabled={busy} size="sm" variant="outline" className="border-white/20 text-gray-400" data-testid="btn-cancel-run">
                Cancelar
              </Button>
            </>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-3 leading-relaxed">
          🕐 <b>Cronómetro persistente</b>: el tiempo lo controla el servidor. Puedes cerrar la app o el navegador y al volver la carrera seguirá corriendo hasta que la termines.<br />
          📍 <b>Mapa GPS</b>: OpenStreetMap muestra tu ubicación, ruta y marcadores cada km.
          {!("geolocation" in (typeof navigator !== "undefined" ? navigator : {})) && (
            <span className="text-red-400 ml-2">⚠ Tu navegador no soporta GPS.</span>
          )}
        </p>

        {/* Toggles: Wake Lock + Podcast Coach */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between gap-3 bg-black/30 border border-white/10 rounded p-3" data-testid="wake-lock-row">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-[#CCFF00] font-bold flex items-center gap-1">
                🔦 Wake Lock
              </p>
              <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                Mantén la pantalla encendida durante el trote (no se apagará sola).
              </p>
            </div>
            <Switch
              checked={wakeLockOn}
              onCheckedChange={toggleWakeLock}
              data-testid="toggle-wake-lock"
            />
          </div>

          <div className="flex items-center justify-between gap-3 bg-black/30 border border-white/10 rounded p-3" data-testid="coach-row">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-[#CCFF00] font-bold flex items-center gap-1">
                🎧 Podcast Coach DYD
                {!coachEnabled && (
                  <span className="ml-1 px-1.5 py-0.5 rounded text-[8px] uppercase font-black bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">Premium</span>
                )}
              </p>
              <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                Coach de voz que te lee tus KPIs cada 1 km + frase motivacional.
              </p>
            </div>
            <Switch
              checked={coachOn && coachEnabled}
              onCheckedChange={toggleCoach}
              data-testid="toggle-coach"
            />
          </div>
        </div>
      </div>

      {/* Coach paywall modal */}
      <Dialog open={coachPaywallOpen} onOpenChange={setCoachPaywallOpen}>
        <DialogContent className="bg-[#0A0A0A] border-[#CCFF00]/30 max-w-md" data-testid="coach-paywall-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
              🎧 Podcast Coach DYD
              <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-black bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">Premium</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-300">
            <p>
              El Podcast Coach DYD es una función <b className="text-[#CCFF00]">premium</b> con costo.
              Un coach de voz te acompaña durante toda la carrera, leyéndote tus KPIs cada kilómetro
              (distancia, tiempo, ritmo) y motivándote con frases exclusivas.
            </p>
            <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded p-3">
              <p className="text-[11px] uppercase tracking-widest text-[#CCFF00] font-bold mb-1">Contrata este servicio</p>
              <a
                href="mailto:info@disfrutaydiviertete.com?subject=Contratación%20Podcast%20Coach%20DYD%20Run"
                className="text-[#CCFF00] font-bold underline break-all"
                data-testid="coach-contact-email"
              >
                info@disfrutaydiviertete.com
              </a>
              <p className="text-[11px] text-gray-400 mt-2">
                Envíanos un correo y activaremos tu Coach en menos de 24 h.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => setCoachPaywallOpen(false)}
                variant="outline"
                className="flex-1 border-white/20"
                data-testid="coach-paywall-close"
              >
                Entendido
              </Button>
              <a
                href="mailto:info@disfrutaydiviertete.com?subject=Contratación%20Podcast%20Coach%20DYD%20Run"
                className="flex-1"
                data-testid="coach-paywall-contact-btn"
              >
                <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 font-bold">
                  Contactar ahora
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───── Manual Import ─────

function ImportPanel({ onImported }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/dyd-run/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importada: ${fmtKm(r.data.distance_m)}km · +${r.data.dyd_earned} DYD`);
      window.dispatchEvent(new Event("dyd-coin-refresh"));
      onImported?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al importar"); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="bg-white/5 border border-white/10 p-4 space-y-3" data-testid="import-panel">
      <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white flex items-center gap-2">
        <Upload size={20} className="text-[#CCFF00]" /> Importar carrera (GPX)
      </h3>
      <p className="text-xs text-gray-400">
        Exporta tu actividad desde Apple Watch (Activity → Share → GPX), Suunto App, Garmin Connect, Strava, etc. y súbela aquí.
      </p>
      <input ref={fileRef} type="file" accept=".gpx,.tcx,.xml" onChange={(e) => handleFile(e.target.files?.[0])} disabled={busy}
        className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#CCFF00] file:text-black file:font-semibold file:uppercase file:tracking-wider file:cursor-pointer"
        data-testid="import-file" />
      {busy && <p className="text-xs text-yellow-300">Procesando…</p>}
    </div>
  );
}

// ───── Strava Panel ─────

function StravaPanel({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get("/dyd-run/strava/status"); setStatus(r.data); }
    catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/dyd-run/strava/connect");
      window.location.href = r.data.authorize_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Strava?")) return;
    try { await api.delete("/dyd-run/strava/disconnect"); toast.success("Desconectado"); load(); }
    catch (e) { toast.error("Error"); }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/dyd-run/strava/sync");
      toast.success(`Sincronizadas: ${r.data.imported} nuevas, ${r.data.skipped} ya existentes`);
      onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al sincronizar"); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  if (!status.available) {
    return (
      <div className="bg-white/5 border border-white/10 p-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
          <FlagBanner size={18} /> Strava (Apple Watch / Suunto / Garmin)
        </h3>
        <p className="text-xs text-gray-500 mt-2">El admin aún no ha configurado las credenciales de Strava. Pídele que las active en Admin → DYD Run Config.</p>
      </div>
    );
  }
  return (
    <div className="bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/30 p-4" data-testid="strava-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-orange-300 flex items-center gap-2">
            <FlagBanner size={18} weight="fill" /> Strava — Apple Watch / Suunto / Garmin
          </h3>
          {status.connected ? (
            <p className="text-xs text-gray-300 mt-1">
              ✓ Conectado como <b>{status.athlete?.firstname} {status.athlete?.lastname}</b>
            </p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">Conecta tu cuenta para sincronizar automáticamente actividades de tu reloj.</p>
          )}
        </div>
        <div className="flex gap-2">
          {status.connected ? (
            <>
              <Button onClick={sync} disabled={busy} variant="outline" size="sm" data-testid="btn-strava-sync">
                <ArrowsClockwise size={14} className="mr-1" /> Sincronizar
              </Button>
              <Button onClick={disconnect} variant="ghost" size="sm" className="text-red-400" data-testid="btn-strava-disconnect">
                Desconectar
              </Button>
            </>
          ) : (
            <Button onClick={connect} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-strava-connect">
              Conectar con Strava
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ───── Runs list ─────

function RunCard({ run, onChanged }) {
  const [publishing, setPublishing] = useState(false);
  const toggle = async () => {
    setPublishing(true);
    try {
      await api.put(`/dyd-run/run/${run.id}/publish`, { is_public: !run.is_public });
      toast.success(run.is_public ? "Carrera privada" : "Carrera publicada al feed");
      onChanged?.();
    } catch (e) { toast.error("Error"); }
    finally { setPublishing(false); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar esta carrera?")) return;
    try { await api.delete(`/dyd-run/run/${run.id}`); toast.success("Eliminada"); onChanged?.(); }
    catch (e) { toast.error("Error"); }
  };
  return (
    <div className="bg-white/5 border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`run-row-${run.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-white/10 text-xs uppercase">
            {run.source === "strava" ? "Strava" : run.source === "manual" ? "GPX" : "Live"}
          </Badge>
          <span className="font-bold text-white">{fmtKm(run.distance_m)} km</span>
          <span className="text-gray-400 text-sm">· {fmtTime(run.duration_sec)}</span>
          <span className="text-gray-400 text-sm">· {fmtPace(run.avg_pace_sec_per_km)}</span>
          {(run.dyd_earned || 0) > 0 && (
            <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border border-[#CCFF00]/40 text-xs">
              <Coin size={12} className="mr-1" /> +{run.dyd_earned}
            </Badge>
          )}
          {run.is_public ? (
            <Badge className="bg-green-500/20 text-green-300 text-xs"><Eye size={10} className="mr-1" /> Público</Badge>
          ) : (
            <Badge className="bg-gray-500/20 text-gray-400 text-xs"><EyeSlash size={10} className="mr-1" /> Privado</Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">{fmtDate(run.started_at)}</p>
        {(run.achievements || []).length > 0 && (
          <div className="flex gap-1 mt-1 text-xs">
            {run.achievements.map((a) => <span key={a.key} title={a.name}>{a.icon}</span>)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-400">Público</Label>
          <Switch checked={!!run.is_public} onCheckedChange={toggle} disabled={publishing} data-testid={`switch-public-${run.id}`} />
        </div>
        <Button size="sm" variant="ghost" onClick={remove} className="text-red-400" data-testid={`btn-delete-${run.id}`}>
          <Trash size={14} />
        </Button>
      </div>
    </div>
  );
}

// ───── Stats Card ─────

function StatsBar({ stats, kpis }) {
  if (!stats) return null;
  const KPI_MAP = {
    total_km: { label: "Km totales", value: fmtKm((stats.total_km || 0) * 1000).slice(0), suffix: "km", icon: MapPin, color: "text-white" },
    total_runs: { label: "Carreras", value: stats.total_runs, icon: PersonSimpleRun, color: "text-white" },
    total_seconds: { label: "Tiempo", value: fmtTime(stats.total_seconds), icon: Timer, color: "text-white" },
    total_dyd: { label: "DYD ganados", value: stats.total_dyd, icon: Coin, color: "text-[#CCFF00]" },
  };
  const visible = (kpis || Object.keys(KPI_MAP).map((k) => ({ key: k, visible: true, label: KPI_MAP[k].label })))
    .filter((k) => k.visible !== false && KPI_MAP[k.key]);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-bar">
      {visible.map((k) => {
        const s = KPI_MAP[k.key];
        return (
          <div key={k.key} className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-400">
              <s.icon size={14} weight="fill" className="text-[#CCFF00]" /> {k.label || s.label}
            </div>
            <div className={`text-3xl font-['Barlow_Condensed'] font-black ${s.color} mt-1`}>{s.value}{s.suffix && <span className="text-sm text-gray-400 ml-1">{s.suffix}</span>}</div>
          </div>
        );
      })}
    </div>
  );
}

// ───── Wheel Navigator (dashboard hero) ─────
// Layout: center "Iniciar Carrera" + arc slots — dynamic based on layout config.

function DydRunWheel({ tab, onTab, onStart, onOpenRaces, sections }) {
  const ICONS = {
    tracker: PlayCircle,
    carreras: Trophy,
    runs: PersonSimpleRun,
    ranking: Trophy,
    community: UsersThree,
    feed: Heartbeat,
    challenges: Target,
  };
  const visible = (sections || []).filter((s) => s.visible !== false);
  const n = Math.max(visible.length, 1);
  return (
    <div className="relative w-full aspect-square max-w-[420px] mx-auto my-2" data-testid="dyd-run-wheel">
      {/* Ring segments as small dots */}
      <div className="absolute inset-0 rounded-full border border-[#CCFF00]/20" />
      <div className="absolute inset-4 rounded-full border-2 border-dashed border-[#CCFF00]/15" />

      {visible.map((it, i) => {
        const angle = (i / n) * 360 - 90; // start at top
        const rad = (angle * Math.PI) / 180;
        const r = 42; // percent
        const x = 50 + r * Math.cos(rad);
        const y = 50 + r * Math.sin(rad);
        const isActive = tab === it.key;
        const Icon = ICONS[it.key] || PlayCircle;
        return (
          <button
            key={it.key}
            onClick={() => (it.key === "carreras" ? onOpenRaces?.() : onTab(it.key))}
            className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center transition-all ${isActive ? "scale-110" : "hover:scale-105"}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            data-testid={`wheel-${it.key}`}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${isActive ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-lg shadow-[#CCFF00]/40" : "bg-white/5 text-white border-white/20 hover:border-[#CCFF00]/60"}`}>
              <Icon size={22} weight="fill" />
            </div>
            <p className={`text-[10px] uppercase tracking-wider mt-1 font-bold ${isActive ? "text-[#CCFF00]" : "text-gray-300"}`}>{it.label}</p>
          </button>
        );
      })}

      {/* Center — Iniciar Carrera */}
      <button
        onClick={onStart}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-[#CCFF00] text-black font-black uppercase tracking-tight text-lg leading-tight flex items-center justify-center border-4 border-black shadow-[0_0_30px_rgba(204,255,0,0.55)] hover:scale-105 transition-transform"
        data-testid="wheel-start-race"
      >
        <span className="font-['Barlow_Condensed'] text-2xl">Iniciar<br/>Carrera</span>
      </button>
    </div>
  );
}

// ───── Main Page ─────

export default function DydRun() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("tracker");
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [feed, setFeed] = useState([]);
  const [available, setAvailable] = useState(true);
  const [access, setAccess] = useState({ is_manager: false, permission_level: null, is_admin: false });
  const [layout, setLayout] = useState(null);
  const [layoutOpen, setLayoutOpen] = useState(false);

  const loadRuns = useCallback(async () => {
    try { const r = await api.get("/dyd-run/my-runs"); setRuns(r.data.runs || []); }
    catch (e) {
      if (e.response?.status === 403) setAvailable(false);
    }
  }, []);
  const loadStats = useCallback(async () => {
    try { const r = await api.get("/dyd-run/stats"); setStats(r.data); } catch {}
  }, []);
  const loadRanking = useCallback(async () => {
    try { const r = await api.get("/dyd-run/ranking"); setRanking(r.data.week_ranking || []); } catch {}
  }, []);
  const loadFeed = useCallback(async () => {
    try { const r = await api.get("/dyd-run/feed"); setFeed(r.data.runs || []); } catch {}
  }, []);
  const loadAccess = useCallback(async () => {
    try { const r = await api.get("/dyd-run/me/is-manager"); setAccess(r.data); } catch {}
  }, []);
  const loadLayout = useCallback(async () => {
    try { const r = await api.get("/dyd-run/layout"); setLayout(r.data); } catch {}
  }, []);

  useEffect(() => {
    loadRuns(); loadStats(); loadRanking(); loadFeed(); loadAccess(); loadLayout();
  }, [loadRuns, loadStats, loadRanking, loadFeed, loadAccess, loadLayout]);

  // Strava callback param feedback
  useEffect(() => {
    if (params.get("strava") === "connected") toast.success("¡Strava conectado! Sincroniza tus actividades.");
    if (params.get("strava_error")) toast.error("Error con Strava: " + params.get("strava_error"));
  }, [params]);

  const refreshAll = () => { loadRuns(); loadStats(); loadRanking(); loadFeed(); };

  if (!available) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="dyd-run-disabled">
        <PersonSimpleRun size={48} className="mx-auto mb-3" />
        <p>DYD Run está temporalmente desactivado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dyd-run-page">
      <div className="flex items-center gap-3 flex-wrap">
        <PersonSimpleRun size={40} weight="fill" className="text-[#CCFF00] shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl md:text-4xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">{layout?.hero_title || "DYD Run"}</h1>
          <p className="text-xs sm:text-sm text-gray-400 leading-snug">{layout?.hero_subtitle || "Tu tracker de carreras · GPS live · Strava sync · Gana DYD por cada km"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <QuickReportButton section="dyd_run" variant="compact" />
          {(user?.role === "admin" || access.is_manager) && (
            <Button variant="outline" size="sm" onClick={() => setLayoutOpen(true)} className="text-xs uppercase border-white/20 text-white hover:bg-white/10" data-testid="dyd-run-edit-layout-btn">
              <GearSix size={14} className="mr-1" /> Editar Layout
            </Button>
          )}
        </div>
      </div>

      <StatsBar stats={stats} kpis={layout?.kpis} />

      <DydRunWheel
        tab={tab}
        onTab={(k) => { setTab(k); const el = document.getElementById("dyd-run-tabs"); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
        onStart={() => { setTab("tracker"); const el = document.getElementById("dyd-run-tabs"); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
        onOpenRaces={() => (window.location.href = "/dashboard/dyd-run/carreras")}
        sections={layout?.sections}
      />

      <div id="dyd-run-tabs">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="tracker" data-testid="tab-tracker"><PlayCircle size={14} className="mr-1" /> Tracker</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs"><PersonSimpleRun size={14} className="mr-1" /> Mis carreras</TabsTrigger>
          <TabsTrigger value="carreras" data-testid="tab-carreras"><Trophy size={14} className="mr-1" /> Eventos</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-ranking"><Trophy size={14} className="mr-1" /> Ranking</TabsTrigger>
          <TabsTrigger value="community" data-testid="tab-community"><UsersThree size={14} className="mr-1" /> Comunidad</TabsTrigger>
          <TabsTrigger value="feed" data-testid="tab-feed"><Heartbeat size={14} className="mr-1" /> Carreras públicas</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="tab-challenges"><Target size={14} className="mr-1" /> Retos</TabsTrigger>
        </TabsList>

        <TabsContent value="tracker" className="space-y-4 mt-4">
          <LiveTrackerTab onFinished={refreshAll} />
          <ImportPanel onImported={refreshAll} />
          <StravaPanel onSynced={refreshAll} />
        </TabsContent>

        <TabsContent value="runs" className="space-y-3 mt-4">
          {runs.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Aún no tienes carreras. Inicia una desde la pestaña Tracker o importa un GPX.</p>
          ) : (
            <>
              {runs.map((r) => <RunCard key={r.id} run={r} onChanged={refreshAll} />)}
            </>
          )}
          {(stats?.achievements || []).length > 0 && (
            <div className="bg-white/5 border border-white/10 p-4 mt-4">
              <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Trophy size={14} /> Tus logros</h3>
              <div className="flex flex-wrap gap-2">
                {stats.achievements.map((a) => (
                  <Badge key={a.key} className="bg-amber-500/20 text-amber-200 border border-amber-500/30">
                    <span className="mr-1">{a.icon}</span> {a.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ranking" className="mt-4">
          <div className="bg-white/5 border border-white/10 p-4" data-testid="ranking-list">
            <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white mb-3 flex items-center gap-2">
              <Trophy size={18} className="text-amber-400" /> Ranking semanal (últimos 7 días)
            </h3>
            {ranking.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Sin actividad en la última semana.</p>
            ) : (
              <div className="divide-y divide-white/10">
                {ranking.map((r, i) => (
                  <div key={r.user_id} className={`flex items-center gap-3 py-2 ${r.user_id === user?.id ? "bg-[#CCFF00]/5" : ""}`}>
                    <div className={`w-7 text-center font-bold ${i < 3 ? "text-[#CCFF00]" : "text-gray-400"}`}>{i + 1}</div>
                    <div className="flex-1 truncate">{r.user_name || "Anónimo"}</div>
                    <Badge className="bg-white/10 text-xs">{r.runs_count} carreras</Badge>
                    <div className="font-mono font-bold text-white w-20 text-right">{r.total_km} km</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="carreras" className="mt-4">
          <div className="text-center py-8">
            <Trophy size={40} className="mx-auto text-[#CCFF00] mb-2" weight="fill" />
            <h3 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Eventos y Carreras</h3>
            <p className="text-sm text-gray-400 mb-4">Regístrate a carreras oficiales o administra la tuya.</p>
            <Button onClick={() => (window.location.href = "/dashboard/dyd-run/carreras")} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="dyd-run-open-races">
              Ver carreras
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="community" className="mt-4">
          <CommunityTab user={user} />
        </TabsContent>

        <TabsContent value="feed" className="mt-4 space-y-3">
          {feed.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Nadie ha publicado carreras todavía. ¡Sé el primero! Marca "Público" en una de tus carreras.</p>
          ) : (
            feed.map((r) => (
              <div key={r.id} className="bg-white/5 border border-white/10 p-3 flex items-center gap-3" data-testid={`feed-${r.id}`}>
                <PersonSimpleRun size={24} className="text-[#CCFF00] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white truncate">{r.user_name || "Corredor"}</div>
                  <div className="text-xs text-gray-400">{fmtDate(r.finished_at)} · {fmtKm(r.distance_m)} km · {fmtPace(r.avg_pace_sec_per_km)}</div>
                </div>
                {(r.achievements || []).length > 0 && (
                  <div className="flex gap-1">{r.achievements.map((a) => <span key={a.key} title={a.name}>{a.icon}</span>)}</div>
                )}
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="challenges" className="mt-4">
          <ChallengesPanel />
        </TabsContent>
      </Tabs>
      </div>

      {(user?.role === "admin" || access.is_manager) && <DydRunAdminActions />}
      {(user?.role === "admin" || access.is_manager) && <StravaAdminConfig />}
      {user?.role === "admin" && <DydRunManagersSection />}
      {(user?.role === "admin" || access.is_manager) && (
        <DydRunLayoutEditor open={layoutOpen} onClose={() => setLayoutOpen(false)} layout={layout} onSaved={(newLayout) => { setLayout(newLayout); setLayoutOpen(false); toast.success("Layout guardado"); }} />
      )}
    </div>
  );
}

function ChallengesPanel() {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dyd-run/challenges");
      setChallenges(r.data.challenges || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const join = async (c) => {
    if (c.entry_fee_dyd > 0 && !window.confirm(`Te cobrarán ${c.entry_fee_dyd} DYD para participar. ¿Continuar?`)) return;
    setBusy(true);
    try {
      await api.post(`/dyd-run/challenges/${c.id}/join`);
      toast.success(`¡Inscrito en "${c.name}"!`);
      window.dispatchEvent(new Event("dyd-coin-refresh"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const openBoard = async (c) => {
    setOpenId(openId === c.id ? null : c.id);
    if (openId === c.id) { setLeaderboard(null); return; }
    try {
      const r = await api.get(`/dyd-run/challenges/${c.id}/leaderboard`);
      setLeaderboard(r.data);
    } catch (e) { toast.error("Error"); }
  };

  if (challenges.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Target size={40} className="mx-auto mb-2" />
        <p className="text-sm italic">No hay retos activos por el momento.</p>
        <p className="text-xs mt-1">El admin puede crear retos desde Plataforma → DYD Run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="challenges-list">
      {challenges.map((c) => {
        const progress = c.type === "team_pool" && c.target_km > 0
          ? null : null;
        return (
          <div key={c.id} className="bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/30 p-4" data-testid={`challenge-${c.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="text-3xl">{c.type === "team_pool" ? "🤝" : "🏆"}</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-['Barlow_Condensed'] font-black text-white uppercase tracking-wider">{c.name}</h3>
                {c.description && <p className="text-xs text-gray-300 mt-1">{c.description}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className="bg-white/10 text-xs">
                    {c.type === "team_pool" ? "Comunitario" : "Individual"}
                  </Badge>
                  <Badge className="bg-[#CCFF00]/15 text-[#CCFF00] border border-[#CCFF00]/40 text-xs">
                    <Coin size={10} className="mr-1" /> Premio: {c.prize_pool_dyd} DYD
                  </Badge>
                  <Badge className="bg-white/10 text-xs">
                    <Users size={10} className="mr-1" /> {c.participant_count} corredores
                  </Badge>
                  {c.entry_fee_dyd > 0 && (
                    <Badge className="bg-orange-500/15 text-orange-300 border border-orange-500/40 text-xs">
                      Inscripción: {c.entry_fee_dyd} DYD
                    </Badge>
                  )}
                  {c.type === "team_pool" && c.target_km > 0 && (
                    <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/40 text-xs">
                      <Target size={10} className="mr-1" /> Meta: {c.target_km} km
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Termina: {fmtDate(c.end_date)}
                </p>
              </div>
              <div className="flex gap-2">
                {c.is_joined ? (
                  <Badge className="bg-green-500/20 text-green-300 border border-green-500/40">✓ Inscrito</Badge>
                ) : (
                  <Button size="sm" onClick={() => join(c)} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase" data-testid={`btn-join-${c.id}`}>
                    Unirme
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => openBoard(c)} data-testid={`btn-board-${c.id}`}>
                  <Trophy size={14} className="mr-1" /> {openId === c.id ? "Ocultar" : "Tabla"}
                </Button>
              </div>
            </div>

            {openId === c.id && leaderboard && (
              <div className="mt-3 pt-3 border-t border-amber-500/20" data-testid={`leaderboard-${c.id}`}>
                {leaderboard.target_progress_pct != null && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Progreso comunitario</span>
                      <span className="font-mono font-bold text-[#CCFF00]">{leaderboard.team_total_km} / {c.target_km} km · {leaderboard.target_progress_pct}%</span>
                    </div>
                    <div className="h-2 bg-black/40 rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#CCFF00] to-amber-400" style={{ width: `${leaderboard.target_progress_pct}%` }} />
                    </div>
                  </div>
                )}
                {leaderboard.leaderboard.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Sin participantes todavía. ¡Sé el primero!</p>
                ) : (
                  <div className="divide-y divide-white/10">
                    {leaderboard.leaderboard.map((row, i) => (
                      <div key={row.user_id} className={`flex items-center gap-3 py-2 ${row.user_id === user?.id ? "bg-[#CCFF00]/5 -mx-2 px-2" : ""}`}>
                        <div className={`w-7 text-center font-bold ${i === 0 ? "text-amber-400" : i < 3 ? "text-[#CCFF00]" : "text-gray-400"}`}>
                          {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                        </div>
                        <div className="flex-1 truncate text-sm">{row.user_name || "Anónimo"}</div>
                        <Badge className="bg-white/10 text-xs">{row.runs} carreras</Badge>
                        <div className="font-mono font-bold text-white w-20 text-right">{row.km} km</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ───── Strava Admin Config (only for admin) ─────

function StravaAdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/dyd-run/strava/config"); setCfg(r.data); setForm((p) => ({ ...p, client_id: r.data.client_id || "" })); }
    catch { /* not admin */ }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/dyd-run/strava/config", form);
      toast.success("Strava configurado");
      setForm((p) => ({ ...p, client_secret: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white/5 border border-white/10 p-4">
      <button onClick={() => setOpen((o) => !o)} className="text-xs uppercase tracking-wider text-gray-400 hover:text-[#CCFF00] flex items-center gap-2" data-testid="btn-toggle-strava-cfg">
        <GearSix size={14} /> {open ? "Ocultar" : "Configurar"} Strava (admin)
      </button>
      {open && cfg && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Strava Client ID</Label>
              <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="123456" className="bg-black/50 border-white/10" data-testid="strava-client-id" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Client Secret</Label>
              <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={cfg.client_secret_masked || "client secret"} className="bg-black/50 border-white/10 font-mono" data-testid="strava-secret" />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            <b>Redirect URI</b> a registrar en Strava: <code className="text-[#CCFF00]">{cfg.redirect_uri || ""}</code><br />
            Obtén credenciales en <a href="https://www.strava.com/settings/api" target="_blank" rel="noreferrer" className="text-[#CCFF00] hover:underline">strava.com/settings/api</a>. Authorization Callback Domain: {(cfg.redirect_uri || "").replace(/^https?:\/\//, "").split("/")[0]}
          </p>
          <Button onClick={save} disabled={busy} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="btn-save-strava-cfg">
            Guardar
          </Button>
        </div>
      )}
    </div>
  );
}

// ───── DYD Run Managers Section (admin only — delegar acceso) ─────

function DydRunManagersSection() {
  const [open, setOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("full");

  const load = useCallback(async () => {
    try { const r = await api.get("/dyd-run/managers"); setManagers(r.data || []); } catch {}
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => {
      api.get(`/dyd-run/managers/candidates?q=${encodeURIComponent(q)}`).then(({ data }) => setCandidates(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(tm);
  }, [q, open]);

  const activeIds = new Set(managers.map((m) => m.user_id));
  const filtered = candidates.filter((c) => !activeIds.has(c.id));

  const grant = async (uid) => {
    try {
      await api.post("/dyd-run/managers", { user_id: uid, permission_level: level });
      toast.success(`Autorizado (${level === "full" ? "acceso total" : "solo registros"})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const revoke = async (uid) => {
    try { await api.delete(`/dyd-run/managers/${uid}`); toast.success("Removido"); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="bg-white/5 border border-white/10 p-4 mt-4" data-testid="dyd-run-managers-section">
      <button onClick={() => setOpen((o) => !o)} className="text-xs uppercase tracking-wider text-gray-400 hover:text-[#CCFF00] flex items-center gap-2" data-testid="btn-toggle-dyd-managers">
        <LockKey size={14} /> {open ? "Ocultar" : "Configurar"} Directivos autorizados DYD Run (admin)
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <p className="text-xs text-gray-400">Delegar acceso al Dashboard admin de DYD Run. Solo verán DYD Run, no el resto de la plataforma.</p>

          <div>
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Autorizados ({managers.length})</p>
            {managers.length === 0 ? (
              <p className="text-sm italic text-gray-500">Sin autorizados. Solo el admin puede ver el dashboard.</p>
            ) : (
              <ul className="space-y-1.5">
                {managers.map((m) => (
                  <li key={m.user_id} className="flex items-center gap-2 p-2 border border-white/10 rounded bg-black/20" data-testid={`dyd-manager-${m.user_id}`}>
                    <UserAvatar src={m.user?.profile_photo_url} fallback={(m.user?.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                    <span className="flex-1 text-sm font-bold text-white">{m.user?.name || m.user_id}</span>
                    <Badge className="text-[9px] uppercase" style={{ background: m.permission_level === "full" ? "#CCFF00" : "#8a8a8a", color: "#000" }}>
                      {m.permission_level === "full" ? "Total" : "Registros"}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={() => revoke(m.user_id)} className="text-red-400 h-7 text-[10px] border-red-400/40" data-testid={`dyd-manager-revoke-${m.user_id}`}>Revocar</Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Nivel a otorgar</p>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setLevel("full")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "full" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-white border-white/20"}`} data-testid="dyd-mgr-level-full">Acceso Total</button>
              <button type="button" onClick={() => setLevel("registration")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "registration" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-white border-white/20"}`} data-testid="dyd-mgr-level-reg">Solo Registros</button>
            </div>
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">Añadir directivo</p>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email…" className="bg-black/50 border-white/10 mb-2" data-testid="dyd-mgr-search" />
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-2 p-2 border border-white/10 rounded">
                  <UserAvatar src={c.profile_photo_url} fallback={(c.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                  <span className="flex-1 text-sm text-white">{c.name} <span className="text-[10px] text-gray-500 uppercase">({c.role})</span></span>
                  <Button size="sm" onClick={() => grant(c.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-7 text-[10px]" data-testid={`dyd-mgr-add-${c.id}`}>Autorizar</Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}


// ───── DYD Run Layout Editor (reorder + visibility + hero text) ─────


// ───── DYD Run Admin Actions (Settings + Challenges CRUD) — admin & managers ─────

function DydRunAdminActions() {
  const [open, setOpen] = useState(false);
  const [perKm, setPerKm] = useState(0);
  const [defaultPerKm, setDefaultPerKm] = useState(0);
  const [challenges, setChallenges] = useState([]);
  const [newChallenge, setNewChallenge] = useState({ name: "", description: "", target_km: 5, reward_dyd: 50, ends_in_days: 7 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingCh, setSavingCh] = useState(false);

  const loadSettings = useCallback(async () => {
    try { const { data } = await api.get("/dyd-run/admin/settings"); setPerKm(data.dyd_per_km); setDefaultPerKm(data.default); }
    catch {}
  }, []);
  const loadChallenges = useCallback(async () => {
    const pick = (d) => Array.isArray(d) ? d : (d?.challenges || d?.items || []);
    try { const { data } = await api.get("/dyd-run/challenges?scope=all"); setChallenges(pick(data)); }
    catch { try { const { data } = await api.get("/dyd-run/challenges"); setChallenges(pick(data)); } catch {} }
  }, []);
  useEffect(() => { if (open) { loadSettings(); loadChallenges(); } }, [open, loadSettings, loadChallenges]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/dyd-run/admin/settings", { dyd_per_km: Number(perKm) }); toast.success("Configuración guardada"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingSettings(false); }
  };

  const createChallenge = async () => {
    if (!newChallenge.name.trim()) return toast.error("Nombre requerido");
    setSavingCh(true);
    try {
      const now = new Date();
      const end = new Date(now.getTime() + Number(newChallenge.ends_in_days || 7) * 24 * 60 * 60 * 1000);
      const iso = (d) => d.toISOString().slice(0, 10);
      await api.post("/dyd-run/admin/challenges", {
        name: newChallenge.name.trim(),
        description: newChallenge.description.trim(),
        target_km: Number(newChallenge.target_km),
        seed_prize_dyd: Number(newChallenge.reward_dyd),
        start_date: iso(now),
        end_date: iso(end),
      });
      toast.success("Reto creado");
      setNewChallenge({ name: "", description: "", target_km: 5, reward_dyd: 50, ends_in_days: 7 });
      loadChallenges();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingCh(false); }
  };
  const closeChallenge = async (cid) => {
    if (!window.confirm("¿Cerrar este reto?")) return;
    try { await api.post(`/dyd-run/admin/challenges/${cid}/close`); toast.success("Cerrado"); loadChallenges(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteChallenge = async (cid) => {
    if (!window.confirm("¿Eliminar este reto?")) return;
    try { await api.delete(`/dyd-run/admin/challenges/${cid}`); toast.success("Eliminado"); loadChallenges(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="bg-white/5 border border-white/10 p-4 mt-4 rounded" data-testid="dyd-run-admin-actions">
      <button onClick={() => setOpen((o) => !o)} className="text-xs uppercase tracking-wider text-gray-400 hover:text-[#CCFF00] flex items-center gap-2" data-testid="btn-toggle-dyd-admin-actions">
        <PencilSimple size={14} /> {open ? "Ocultar" : "Modificar y Crear"} — Admin/Directivo autorizado
      </button>
      {open && (
        <div className="mt-4 space-y-6">
          {/* Settings */}
          <div>
            <p className="text-xs uppercase tracking-wider text-[#CCFF00] mb-2 font-bold">Configuración DYD por km</p>
            <div className="flex items-center gap-2">
              <Input type="number" value={perKm} onChange={(e) => setPerKm(e.target.value)} className="bg-black/50 border-white/10 text-white max-w-24" data-testid="dyd-per-km-input" />
              <span className="text-xs text-gray-500">DYD/km (default: {defaultPerKm})</span>
              <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]" data-testid="btn-save-dyd-per-km">
                {savingSettings ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>

          {/* Challenges */}
          <div>
            <p className="text-xs uppercase tracking-wider text-[#CCFF00] mb-2 font-bold">Crear reto</p>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-3">
              <Input placeholder="Nombre" value={newChallenge.name} onChange={(e) => setNewChallenge({ ...newChallenge, name: e.target.value })} className="bg-black/50 border-white/10 text-white sm:col-span-2" data-testid="ch-name" />
              <Input placeholder="Descripción" value={newChallenge.description} onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })} className="bg-black/50 border-white/10 text-white sm:col-span-2" data-testid="ch-desc" />
              <Input type="number" placeholder="Km" value={newChallenge.target_km} onChange={(e) => setNewChallenge({ ...newChallenge, target_km: e.target.value })} className="bg-black/50 border-white/10 text-white" data-testid="ch-km" />
              <Input type="number" placeholder="DYD" value={newChallenge.reward_dyd} onChange={(e) => setNewChallenge({ ...newChallenge, reward_dyd: e.target.value })} className="bg-black/50 border-white/10 text-white" data-testid="ch-dyd" />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-gray-400">Días para expirar:</span>
              <Input type="number" value={newChallenge.ends_in_days} onChange={(e) => setNewChallenge({ ...newChallenge, ends_in_days: e.target.value })} className="bg-black/50 border-white/10 text-white max-w-20" data-testid="ch-days" />
              <Button onClick={createChallenge} disabled={savingCh} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]" data-testid="btn-create-challenge">
                {savingCh ? "Creando…" : "Crear reto"}
              </Button>
            </div>
            <p className="text-xs uppercase tracking-wider text-[#CCFF00] mb-2 font-bold">Retos existentes ({challenges.length})</p>
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {challenges.length === 0 && <li className="text-center text-xs text-gray-500 py-2">Sin retos activos</li>}
              {challenges.map((c) => (
                <li key={c.id} className="flex items-center gap-2 p-2 border border-white/10 rounded bg-black/20" data-testid={`ch-item-${c.id}`}>
                  <span className="flex-1 text-white text-sm">
                    <b>{c.name}</b>
                    <span className="text-[10px] text-gray-500 ml-2">{c.target_km}km · +{c.reward_dyd} DYD · {c.status || "activo"}</span>
                  </span>
                  {c.status !== "closed" && (
                    <Button size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400 h-7 text-[10px]" onClick={() => closeChallenge(c.id)} data-testid={`btn-close-ch-${c.id}`}>Cerrar</Button>
                  )}
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 h-7 text-[10px]" onClick={() => deleteChallenge(c.id)} data-testid={`btn-delete-ch-${c.id}`}>Eliminar</Button>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-gray-500 italic">
            Modificas la configuración de DYD por km y creas/administras retos para todos los corredores.
            Los directivos autorizados por admin pueden usar esta sección igual que un administrador.
          </p>
        </div>
      )}
    </div>
  );
}

function DydRunLayoutEditor({ open, onClose, layout, onSaved }) {
  const [form, setForm] = useState({ hero_title: "", hero_subtitle: "", sections: [], kpis: [] });
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (open && layout) setForm({
      hero_title: layout.hero_title || "",
      hero_subtitle: layout.hero_subtitle || "",
      sections: [...(layout.sections || [])],
      kpis: [...(layout.kpis || [])],
    });
  }, [open, layout]);

  const move = (list, idx, delta) => {
    const arr = [...list];
    const target = idx + delta;
    if (target < 0 || target >= arr.length) return arr;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    return arr;
  };
  const toggleVisible = (list, idx) => list.map((it, i) => i === idx ? { ...it, visible: !it.visible } : it);

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx, listKey) => {
    if (dragIdx === null || dragIdx === idx) return;
    const arr = [...form[listKey]];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, moved);
    setForm((f) => ({ ...f, [listKey]: arr }));
    setDragIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dyd-run/layout", form);
      onSaved?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const resetDefault = () => {
    if (!window.confirm("¿Restablecer al layout por defecto?")) return;
    setForm({
      hero_title: "DYD Run",
      hero_subtitle: "Tu tracker de carreras · GPS live · Strava sync · Gana DYD por cada km",
      sections: [
        { key: "tracker", label: "Tracker", visible: true },
        { key: "carreras", label: "Eventos", visible: true },
        { key: "runs", label: "Mis Carreras", visible: true },
        { key: "ranking", label: "Ranking", visible: true },
        { key: "community", label: "Comunidad", visible: true },
        { key: "feed", label: "Publics", visible: true },
        { key: "challenges", label: "Retos", visible: true },
      ],
      kpis: [
        { key: "total_km", label: "Km totales", visible: true },
        { key: "total_runs", label: "Carreras", visible: true },
        { key: "total_seconds", label: "Tiempo", visible: true },
        { key: "total_dyd", label: "DYD ganados", visible: true },
      ],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-black border-white/20 text-white" data-testid="dyd-layout-editor">
        <DialogHeader>
          <DialogTitle className="text-white font-['Barlow_Condensed'] text-2xl uppercase">Editar Layout DYD Run</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Hero */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-gray-400">Textos del encabezado</p>
            <Field label="Título" hint="Máx 80 chars">
              <Input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} className="bg-black/50 border-white/20 text-white" data-testid="layout-hero-title" />
            </Field>
            <Field label="Subtítulo" hint="Máx 200 chars">
              <Input value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} className="bg-black/50 border-white/20 text-white" data-testid="layout-hero-subtitle" />
            </Field>
          </div>

          {/* Sections */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-gray-400">Pétalos del wheel (arrastra o usa flechas para reordenar)</p>
            <ul className="space-y-1.5">
              {form.sections.map((s, i) => (
                <li key={s.key}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(i, "sections")}
                    className={`flex items-center gap-2 p-2 border rounded cursor-move ${dragIdx === i ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-white/5"}`}
                    data-testid={`layout-section-${s.key}`}>
                  <span className="text-[10px] font-mono text-gray-500 w-6 text-right">{i + 1}</span>
                  <Input value={s.label} onChange={(e) => setForm((f) => ({ ...f, sections: f.sections.map((it, j) => j === i ? { ...it, label: e.target.value } : it) }))} className="bg-black/50 border-white/10 text-white flex-1 h-8 text-xs" />
                  <Switch checked={s.visible !== false} onCheckedChange={() => setForm((f) => ({ ...f, sections: toggleVisible(f.sections, i) }))} data-testid={`layout-section-visible-${s.key}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setForm((f) => ({ ...f, sections: move(f.sections, i, -1) }))} data-testid={`layout-section-up-${s.key}`}>↑</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setForm((f) => ({ ...f, sections: move(f.sections, i, +1) }))} data-testid={`layout-section-down-${s.key}`}>↓</Button>
                </li>
              ))}
            </ul>
          </div>

          {/* KPIs */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-gray-400">KPIs superiores (orden y visibilidad)</p>
            <ul className="space-y-1.5">
              {form.kpis.map((k, i) => (
                <li key={k.key}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(i, "kpis")}
                    className={`flex items-center gap-2 p-2 border rounded cursor-move ${dragIdx === i ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-white/5"}`}
                    data-testid={`layout-kpi-${k.key}`}>
                  <span className="text-[10px] font-mono text-gray-500 w-6 text-right">{i + 1}</span>
                  <Input value={k.label} onChange={(e) => setForm((f) => ({ ...f, kpis: f.kpis.map((it, j) => j === i ? { ...it, label: e.target.value } : it) }))} className="bg-black/50 border-white/10 text-white flex-1 h-8 text-xs" />
                  <Switch checked={k.visible !== false} onCheckedChange={() => setForm((f) => ({ ...f, kpis: toggleVisible(f.kpis, i) }))} data-testid={`layout-kpi-visible-${k.key}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setForm((f) => ({ ...f, kpis: move(f.kpis, i, -1) }))} data-testid={`layout-kpi-up-${k.key}`}>↑</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => setForm((f) => ({ ...f, kpis: move(f.kpis, i, +1) }))} data-testid={`layout-kpi-down-${k.key}`}>↓</Button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-between gap-2 pt-3 border-t border-white/10">
            <Button variant="outline" onClick={resetDefault} className="border-white/20 text-white text-xs" data-testid="layout-reset">Restablecer por defecto</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/20 text-white">Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black font-bold" data-testid="layout-save">
                {saving ? "Guardando…" : "Guardar layout"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Field helper (local to this file for the editor)
function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-bold text-gray-300">{label}</Label>
      {hint && <p className="text-[10px] text-gray-500 mb-1">[{hint}]</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}



// ───── DYD Run Community (General + Mi Perfil) ─────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgoRun(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX");
}

function CommunityTab({ user }) {
  const [sub, setSub] = useState("general");
  const [profile, setProfile] = useState(null);
  const [general, setGeneral] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const loadProfile = useCallback(async () => {
    try { const { data } = await api.get("/dyd-run/community/profile/me"); setProfile(data); } catch { /* noop */ }
  }, []);
  const loadGeneral = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dyd-run/community/feed?limit=50"); setGeneral(data.posts || []); }
    catch { toast.error("Error al cargar el feed"); }
    finally { setLoading(false); }
  }, []);
  const loadMine = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dyd-run/community/mine?limit=50"); setMine(data.posts || []); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); loadGeneral(); loadMine(); }, [loadProfile, loadGeneral, loadMine]);

  const togglePrivacy = async () => {
    const next = !profile?.dyd_run_profile_public;
    try {
      await api.patch("/dyd-run/community/profile/privacy", { public: next });
      setProfile((p) => ({ ...p, dyd_run_profile_public: next }));
      toast.success(next ? "Perfil público" : "Perfil privado");
    } catch { toast.error("Error al cambiar privacidad"); }
  };

  const onCreated = () => { setComposerOpen(false); loadGeneral(); loadMine(); loadProfile(); };
  const posts = sub === "general" ? general : mine;

  return (
    <div className="space-y-4" data-testid="dyd-run-community">
      {/* Privacy toggle bar */}
      <div className="bg-white/5 border border-white/10 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="dyd-run-privacy-bar">
        <div className="flex items-center gap-3 min-w-0">
          <UserAvatar
            src={profile?.profile_photo_url}
            fallback={(profile?.name || "?").slice(0, 2)}
            className="w-10 h-10 rounded-full border border-white/10"
          />
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">{profile?.name || "Corredor"}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest">
              {profile?.public_post_count || 0} públicas · {profile?.post_count || 0} totales
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-400 flex items-center gap-1.5">
            {profile?.dyd_run_profile_public ? <LockKeyOpen size={14} className="text-[#CCFF00]" /> : <LockKey size={14} className="text-orange-400" />}
            Perfil {profile?.dyd_run_profile_public ? "Público" : "Privado"}
          </Label>
          <Switch
            checked={!!profile?.dyd_run_profile_public}
            onCheckedChange={togglePrivacy}
            data-testid="dyd-run-privacy-switch"
          />
          <Button
            onClick={() => setComposerOpen(true)}
            size="sm"
            className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 font-bold uppercase tracking-wider"
            data-testid="dyd-run-new-post-btn"
          >
            <UploadSimple size={14} className="mr-1" /> Publicar
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-white/10">
        <button
          onClick={() => setSub("general")}
          className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors ${sub === "general" ? "border-[#CCFF00] text-[#CCFF00]" : "border-transparent text-gray-400 hover:text-white"}`}
          data-testid="dyd-run-subtab-general"
        >
          <UsersThree size={14} className="inline mr-1" /> General
        </button>
        <button
          onClick={() => setSub("mine")}
          className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors ${sub === "mine" ? "border-[#CCFF00] text-[#CCFF00]" : "border-transparent text-gray-400 hover:text-white"}`}
          data-testid="dyd-run-subtab-mine"
        >
          <PersonSimpleRun size={14} className="inline mr-1" /> Mi perfil
        </button>
      </div>

      {/* Posts list */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-10 text-gray-500" data-testid="dyd-run-community-empty">
          <UsersThree size={40} className="mx-auto mb-2 text-gray-600" />
          <p className="text-sm italic">
            {sub === "general" ? "Nadie ha publicado todavía. ¡Sé el primero!" : "No tienes publicaciones. Comparte tu primera carrera."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid={`dyd-run-community-list-${sub}`}>
          {posts.map((p) => (
            <RunPostCard key={p.id} post={p} currentUser={user} onChanged={() => { loadGeneral(); loadMine(); loadProfile(); }} />
          ))}
        </div>
      )}

      <RunPostComposer open={composerOpen} onClose={() => setComposerOpen(false)} onCreated={onCreated} />
    </div>
  );
}

function RunPostCard({ post, currentUser, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [liking, setLiking] = useState(false);
  const [likes, setLikes] = useState(post.likes || []);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const iLike = likes.includes(currentUser?.id);
  const canDelete = post.user_id === currentUser?.id || currentUser?.role === "admin";
  const isVideo = post.media_type === "video";
  const mediaUrl = post.media_path ? `${BACKEND_URL}/api/dyd-run/community/posts/${post.id}/media` : "";

  const toggleLike = async () => {
    if (liking) return;
    setLiking(true);
    try {
      const { data } = await api.post(`/dyd-run/community/posts/${post.id}/like`);
      setLikes((L) => data.liked ? [...L, currentUser.id] : L.filter((u) => u !== currentUser.id));
    } catch { /* noop */ } finally { setLiking(false); }
  };
  const openComments = async () => {
    setExpanded((s) => !s);
    if (!expanded) {
      try { const { data } = await api.get(`/dyd-run/community/posts/${post.id}/comments`); setComments(data || []); }
      catch { /* noop */ }
    }
  };
  const sendComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    try {
      await api.post(`/dyd-run/community/posts/${post.id}/comments`, { text });
      setNewComment("");
      const { data } = await api.get(`/dyd-run/community/posts/${post.id}/comments`);
      setComments(data || []);
      setCommentCount((c) => c + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al comentar"); }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await api.delete(`/dyd-run/community/posts/${post.id}`);
      toast.success("Eliminada");
      onChanged?.();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <article className="bg-white/5 border border-white/10 overflow-hidden" data-testid={`dyd-run-post-${post.id}`}>
      <header className="p-3 flex items-center gap-3">
        <UserAvatar src={post.user_photo} fallback={(post.user_name || "?").slice(0, 2)} className="w-9 h-9 rounded-full border border-white/10" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{post.user_name || "Corredor"}</p>
          <p className="text-[10px] text-gray-500 flex items-center gap-1">
            {timeAgoRun(post.created_at)}
            {!post.is_public && <span className="inline-flex items-center gap-0.5 text-orange-400 uppercase tracking-widest"><LockKey size={9} /> privado</span>}
            {post.edited_at && <span className="italic">· editado</span>}
          </p>
        </div>
        {canDelete && (
          <Button variant="ghost" size="sm" onClick={remove} className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0" data-testid={`dyd-run-post-delete-${post.id}`}>
            <Trash size={14} />
          </Button>
        )}
      </header>

      {post.media_path && (
        <div className="bg-black">
          {isVideo ? (
            <video src={mediaUrl} controls playsInline className="w-full max-h-[60vh] object-contain bg-black" data-testid={`dyd-run-post-video-${post.id}`}>
              <track kind="captions" />
            </video>
          ) : (
            <img src={mediaUrl} alt={post.text || "post"} className="w-full max-h-[60vh] object-contain bg-black" loading="lazy" />
          )}
        </div>
      )}

      <div className="p-3 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleLike}
            className={`flex items-center gap-1 text-xs uppercase tracking-widest font-bold ${iLike ? "text-red-400" : "text-gray-400 hover:text-white"}`}
            data-testid={`dyd-run-post-like-${post.id}`}
          >
            <Heart size={16} weight={iLike ? "fill" : "regular"} /> {likes.length}
          </button>
          <button
            onClick={openComments}
            className="flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-gray-400 hover:text-white"
            data-testid={`dyd-run-post-comments-${post.id}`}
          >
            <ChatCircle size={16} /> {commentCount}
          </button>
        </div>
        {post.text && <p className="text-sm text-gray-200 whitespace-pre-wrap">{post.text}</p>}

        {expanded && (
          <div className="space-y-2 pt-2 border-t border-white/5" data-testid={`dyd-run-comments-panel-${post.id}`}>
            {comments.length === 0 ? (
              <p className="text-[10px] text-gray-500 italic">Sin comentarios aún.</p>
            ) : comments.map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-xs">
                <UserAvatar src={c.author_photo} fallback={(c.author_name || "?").slice(0, 2)} className="w-6 h-6 rounded-full shrink-0" />
                <div className="flex-1 bg-black/40 p-2 border border-white/10 rounded">
                  <p className="text-white"><b>{c.author_name}</b> {c.text}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{timeAgoRun(c.created_at)}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendComment()}
                placeholder="Escribe un comentario…"
                className="bg-black/60 border-white/15 h-9 text-xs"
                data-testid={`dyd-run-comment-input-${post.id}`}
              />
              <Button onClick={sendComment} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 h-9 px-3" data-testid={`dyd-run-comment-send-${post.id}`}>
                <PaperPlaneRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function RunPostComposer({ open, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => { if (!open) { setText(""); setFile(null); setIsPublic(true); setSubmitting(false); } }, [open]);

  const submit = async () => {
    if (!text.trim() && !file) { toast.error("Escribe un mensaje o adjunta un archivo"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      fd.append("is_public", isPublic ? "true" : "false");
      if (file) fd.append("file", file, file.name);
      await api.post("/dyd-run/community/posts", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Publicación creada");
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al publicar"); }
    finally { setSubmitting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="dyd-run-composer-overlay">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="dyd-run-composer">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Nueva publicación</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 h-7 w-7 p-0"><X size={16} /></Button>
        </div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="¿Qué tal tu carrera hoy?"
          className="bg-black/60 border-white/15 text-sm"
          data-testid="dyd-run-composer-text"
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1 border-[#CCFF00]/40 text-[#CCFF00] h-10 text-xs uppercase" data-testid="dyd-run-composer-file-btn">
            <UploadSimple size={14} className="mr-1" /> Foto/Video
          </Button>
          <Button variant="outline" onClick={() => camRef.current?.click()} className="flex-1 border-[#FFD24A]/40 text-[#FFD24A] h-10 text-xs uppercase" data-testid="dyd-run-composer-cam-btn">
            <Camera size={14} className="mr-1" /> Cámara
          </Button>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dyd-run-composer-file-input" />
          <input ref={camRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dyd-run-composer-cam-input" />
        </div>
        {file && (
          <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded" data-testid="dyd-run-composer-file-info">
            <div className="text-[10px] text-gray-300 truncate flex-1">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>
            <Button variant="ghost" size="sm" onClick={() => setFile(null)} className="text-red-400 h-7 w-7 p-0"><X size={12} /></Button>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="dyd-run-composer-public-switch" />
          <Label className="text-xs text-gray-400 flex items-center gap-1">
            {isPublic ? <LockKeyOpen size={12} className="text-[#CCFF00]" /> : <LockKey size={12} className="text-orange-400" />}
            {isPublic ? "Público en feed General" : "Privado (solo en Mi perfil)"}
          </Label>
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="outline" onClick={onClose} className="text-xs uppercase" data-testid="dyd-run-composer-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={submitting || (!text.trim() && !file)}
            className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 font-bold uppercase"
            data-testid="dyd-run-composer-submit"
          >
            {submitting ? "Publicando…" : "Publicar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
